"use client"

import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { getInsectBySessionAndTrap, allProbeDataFromAccount, renameSession, deleteSession, clearSessionProbeData } from "../redux/slices/dataSlice";
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface ProbeListSessionProps {
  sessionPageNum: number;
  resetSessionPageNum: () => void;
  currentTeam: { id: string; name: string } | null;
}

interface SelectedProbe {
  trapId: string;
  trapName?: string;
  activeSessions: string[];
}

export default function ProbeListSession({ resetSessionPageNum, currentTeam }: ProbeListSessionProps) {
  const { t } = useTranslation(['session', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const { probeDataList, sessionFlag } = useSelector((state: RootState) => state.data);
  const [selectedProbe, setSelectedProbe] = useState<SelectedProbe | null>(null);
  const [selectedProbeId, setSelectedProbeId] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [probeToReselect, setProbeToReselect] = useState<string | null>(null);
  const [sessionToRename, setSessionToRename] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameLoading, setRenameLoading] = useState(false);

  // Debug logging

  // Fix hydration issues
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    resetSessionPageNum();
    setSelectedProbe(null);
    setSelectedProbeId(null);
    setSelectedSession(null);
    dispatch(clearSessionProbeData());
  }, [sessionFlag, resetSessionPageNum]);

  // Re-select probe after data refresh
  useEffect(() => {
    if (probeToReselect && probeDataList?.probes?.[probeToReselect]) {
      setSelectedProbe(probeDataList.probes[probeToReselect]);
      setSelectedProbeId(probeToReselect);
      setProbeToReselect(null); // Clear the re-selection flag
    }
  }, [probeDataList, probeToReselect]);

  const handleClick = (probeId: string) => {
    if (probeDataList?.probes) {
      const probe = probeDataList.probes[probeId];
      setSelectedProbe(probe);
      setSelectedProbeId(probeId);
      setSelectedSession(null);
      dispatch(clearSessionProbeData());
    }
  };

  const handleSessionClick = (session: string) => {
    if (!session) {
      logger.error("Attempted to select null/undefined session");
      return;
    }
    
    resetSessionPageNum();
    setSelectedSession(session);
    if (selectedProbe) {
      const sessionMsg = {
        sessionName: session,
        trapId: selectedProbe.trapId,
      };
      dispatch(getInsectBySessionAndTrap(sessionMsg));
    }
  };

  const formatSessionName = (session: string | null | undefined) => {
    if (!session) {
      return t('unknownSession');
    }
    
    
    // If the session doesn't contain dashes, return it as-is
    if (!session.includes("-")) {
      return session;
    }
    
    const parts = session.split("-");
    
    // Remove the last part (usually timestamp)
    parts.pop();
    const formatted = parts.join("-");
    
    
    // If the formatted name is empty, return the original
    return formatted || session;
  };

  const handleDeleteSession = (session: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSessionToDelete(session);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteSession = async () => {
    if (sessionToDelete && selectedProbe && currentTeam?.name) {
      try {
        const deletePayload = {
          sessionName: sessionToDelete,
          trapId: selectedProbe.trapId,
        };
        const result = await dispatch(deleteSession(deletePayload)).unwrap();
        
        const currentlySelectedProbeId = selectedProbeId;
        
        setShowDeleteConfirmation(false);
        setSessionToDelete(null);
        
        if (selectedSession === sessionToDelete) {
          setSelectedSession(null);
          dispatch(clearSessionProbeData());
        }
        
        const refreshMsg = { teamName: currentTeam.name };
        await dispatch(allProbeDataFromAccount(refreshMsg)).unwrap();
        
        if (currentlySelectedProbeId) {
          setProbeToReselect(currentlySelectedProbeId);
        }
        
        alert(`${t('deleteSuccess')} (${result.deleted} ${t('recordsArchived') || 'records archived'})`);
      } catch (error) {
        logger.error('Failed to delete session:', error);
        alert(t('deleteFailed'));
      }
    }
  };

  const handleRenameSession = (session: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSessionToRename(session);
    setRenameValue(formatSessionName(session));
    setRenameError(null);
  };

  const confirmRenameSession = async () => {
    if (!sessionToRename || !selectedProbe || !currentTeam?.name) return;
    if (!renameValue.trim()) {
      setRenameError(t('renameRequired') || 'Session name is required');
      return;
    }
    setRenameLoading(true);
    setRenameError(null);
    try {
      await dispatch(
        renameSession({
          trapId: selectedProbe.trapId,
          oldSessionName: sessionToRename,
          newSessionName: renameValue.trim(),
        })
      ).unwrap();

      const currentlySelectedProbeId = selectedProbeId;
      setSessionToRename(null);
      if (selectedSession === sessionToRename) {
        setSelectedSession(null);
        dispatch(clearSessionProbeData());
      }
      await dispatch(allProbeDataFromAccount({ teamName: currentTeam.name })).unwrap();
      if (currentlySelectedProbeId) {
        setProbeToReselect(currentlySelectedProbeId);
      }
    } catch (err: unknown) {
      logger.error('Failed to rename session:', err);
      const errData = err as { error?: string } | string;
      const msg =
        typeof errData === 'string'
          ? errData
          : errData?.error || t('renameFailed') || 'Failed to rename session';
      setRenameError(msg);
    } finally {
      setRenameLoading(false);
    }
  };

  const cancelRenameSession = () => {
    setSessionToRename(null);
    setRenameError(null);
  };

  const cancelDeleteSession = () => {
    setShowDeleteConfirmation(false);
    setSessionToDelete(null);
  };

  const sortedProbes = probeDataList?.probes
    ? Object.keys(probeDataList.probes)
        .filter((id) => probeDataList.probes[id] != null)
        .sort((a, b) => {
          const sessionsA = probeDataList.probes[a]?.activeSessions?.length ?? 0;
          const sessionsB = probeDataList.probes[b]?.activeSessions?.length ?? 0;
          return sessionsB - sessionsA;
        })
    : [];

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <div className="flex h-full">
        <div className="w-1/2 p-4 border-r border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('smartProbesHeading')}</h3>
          <div className="text-gray-800 text-center py-8">{t('common:loading')}</div>
        </div>
        <div className="w-1/2 p-4">
          <div className="flex items-center justify-center h-full">
            <h3 className="text-gray-800 text-lg">{t('selectProbe')}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Left Half - Probe List */}
      <div className="w-1/2 p-4 border-r border-gray-200 overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('smartProbesHeading')}</h3>
        <div className="space-y-2">
          {sortedProbes.length > 0 ? (
            sortedProbes.map((probeId) => {
              const probe = probeDataList!.probes[probeId];
              if (!probe) return null;
              const isSelected = selectedProbeId === probeId;
              return (
                <div
                  key={probeId}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                  onClick={() => handleClick(probeId)}
                >
                  <div className="font-medium text-gray-900">
                    SmartProbe {probe.trapName ? probe.trapName : probeId}
                  </div>
                  <div className="text-sm text-gray-800">
                    {t('sessionCount')} {probe.activeSessions?.length ?? 0}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-gray-800 text-center py-8">{t('noSmartProbesFound')}</div>
          )}
        </div>
      </div>

      {/* Right Half - Session List */}
      <div className="w-1/2 p-4 overflow-y-auto">
        {selectedProbe ? (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('savedSessionsFor')} {selectedProbe.trapName || selectedProbe.trapId}
            </h3>
            {selectedProbe.activeSessions && selectedProbe.activeSessions.length > 0 ? (
              <div className="space-y-2">
                {selectedProbe.activeSessions
                  .filter((session: string | null | undefined) => session != null) // Filter out null/undefined sessions
                  .map((session: string) => (
                    <div
                      key={session}
                      className={`p-3 rounded cursor-pointer transition-colors relative group ${
                        selectedSession === session
                          ? 'bg-blue-600 text-white border-2 border-black'
                          : 'bg-blue-500 text-white hover:bg-blue-400'
                      }`}
                      onClick={() => handleSessionClick(session)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium">{formatSessionName(session)}</div>
                          <div className="text-xs opacity-75">{t('rawLabel')} {session}</div>
                        </div>
                        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                          <button
                            onClick={(e) => handleRenameSession(session, e)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-yellow-500 hover:bg-yellow-600 text-white p-1 rounded text-xs cursor-pointer"
                            title={t('renameSessionTitle') || 'Rename session'}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => handleDeleteSession(session, e)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white p-1 rounded text-xs cursor-pointer"
                            title={t('deleteSessionTitle')}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-gray-800 text-center py-8">{t('noSavedSessions')}</div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <h3 className="text-gray-800 text-lg">{t('selectProbe')}</h3>
          </div>
        )}
      </div>

      {/* Rename Session Modal */}
      {sessionToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('renameSessionTitle') || 'Rename Session'}
            </h3>
            <p className="text-sm text-gray-700 mb-2">
              {t('renamingSession') || 'Renaming'}: <strong>{formatSessionName(sessionToRename)}</strong>
            </p>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => { setRenameValue(e.target.value); setRenameError(null); }}
              className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('newSessionNamePlaceholder') || 'New session name'}
              disabled={renameLoading}
            />
            {renameError && (
              <p className="text-sm text-red-600 mb-3">{renameError}</p>
            )}
            <div className="flex space-x-4 mt-4">
              <button
                onClick={cancelRenameSession}
                disabled={renameLoading}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors cursor-pointer disabled:opacity-50"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={confirmRenameSession}
                disabled={renameLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                {renameLoading ? (t('common:loading') || 'Saving…') : (t('renameButton') || 'Rename')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('confirmSessionDeletion')}
            </h3>
            <p className="text-gray-900 mb-2">
              {t('deleteSessionConfirmMessage')}
            </p>
            <p className="text-sm text-gray-800 mb-6">
              <strong>{t('sessionLabel')}</strong> {formatSessionName(sessionToDelete)}
            </p>
            <p className="text-sm text-red-600 mb-6">
              {t('cannotBeUndone')}
            </p>
            <div className="flex space-x-4">
              <button
                onClick={cancelDeleteSession}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={confirmDeleteSession}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors cursor-pointer"
              >
                {t('deleteSessionButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
